import type { DIYMask } from '../services/scanService';
import type { AcneSeverity } from '../services/scanService';

import type { RegionAcneSummary } from '../services/scanService';

/**
 * Generates personalized DIY face mask recommendations based on scan results
 * @param skinConditionFocus - Determines which skin conditions to focus on:
 *   - "acne-oily": Only acne and oily skin masks (Plus)
 *   - "expanded": Acne, oily, dryness, sensitivity masks (Pro)
 *   - "comprehensive": All conditions including eczema, rosacea, hyperpigmentation, aging (Elite)
 */
export function generateDIYMasks(
  glowScore: number,
  acneSeverity: AcneSeverity,
  estimatedPH?: number,
  isInflammatory?: boolean,
  skinConditionFocus: 'acne-oily' | 'expanded' | 'comprehensive' = 'acne-oily',
  regions?: RegionAcneSummary[]
): DIYMask[] {
  const masks: DIYMask[] = [];
  
  // Extract comprehensive data from regions if available
  const regionsData = regions || [];
  const totalActiveLesions = regionsData.reduce((sum, r) => sum + r.activeLesionCount, 0);
  const totalComedones = regionsData.reduce((sum, r) => sum + r.comedoneCount, 0);
  const hyperpigmentationRegions = regionsData.filter(r => r.hyperpigmentationLevel !== 'none');
  const maxHyperpigmentation = hyperpigmentationRegions.length > 0 
    ? hyperpigmentationRegions.reduce((max, r) => {
        const levels = { 'none': 0, 'mild': 1, 'moderate': 2, 'marked': 3 };
        return levels[r.hyperpigmentationLevel] > levels[max.hyperpigmentationLevel] ? r : max;
      }, hyperpigmentationRegions[0])
    : null;
  const textureDescriptions = regionsData.map(r => r.textureDescription.toLowerCase()).join(' ');
  const hasRoughTexture = textureDescriptions.includes('rough') || textureDescriptions.includes('uneven');
  const hasDryTexture = textureDescriptions.includes('dry') || textureDescriptions.includes('dehydrated');
  const hasOilyTexture = textureDescriptions.includes('oily') || textureDescriptions.includes('shiny');
  
  // Create a unique seed based on ALL scan data for personalized mask selection
  const maskSeed = Math.floor(
    (glowScore * 100) +
    ((estimatedPH || 5.0) * 50) +
    (totalActiveLesions * 10) +
    (totalComedones * 5) +
    (hyperpigmentationRegions.length * 20) +
    (regionsData.length * 15) +
    (hasRoughTexture ? 100 : 0) +
    (hasDryTexture ? 50 : 0) +
    (hasOilyTexture ? 75 : 0)
  ) % 10000;

  // For moderate to severe acne - focus on anti-inflammatory and calming masks
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe' || isInflammatory) {
    masks.push({
      id: 'scan-aloe-honey',
      title: 'Aloe + Honey Calming Mask',
      emoji: '🍯',
      description: 'Deeply soothing mask to reduce redness and inflammation from active breakouts.',
      benefits: ['Reduces redness', 'Soothes inflammation', 'Antibacterial', 'Hydrates'],
      ingredients: [
        '2 tbsp pure aloe vera gel',
        '1 tbsp raw honey',
        '1 tsp chamomile tea (cooled)',
        '1 drop tea tree oil (optional, if not too sensitive)'
      ],
      instructions: [
        'Mix aloe vera gel and honey in a clean bowl until smooth',
        'Add cooled chamomile tea and mix well',
        'Add tea tree oil if using (test on small area first)',
        'Apply evenly to clean, dry face',
        'Avoid eye area and any open wounds',
        'Relax for 15-20 minutes',
        'Rinse with cool water and pat dry',
        'Apply gentle moisturizer after'
      ],
      duration: '15-20 min',
      science: 'Aloe vera contains polysaccharides that reduce inflammation and promote healing. Honey has natural antibacterial properties and acts as a humectant. Chamomile provides additional anti-inflammatory benefits.',
      tags: ['Anti-inflammatory', 'Calming', 'Acne-prone', 'Sensitive skin']
    });

    masks.push({
      id: 'scan-green-tea',
      title: 'Green Tea + Oatmeal Soothing Mask',
      emoji: '🍵',
      description: 'Gentle mask to calm irritated skin and reduce redness from active acne.',
      benefits: ['Reduces redness', 'Calms irritation', 'Antioxidant boost', 'Gentle exfoliation'],
      ingredients: [
        '2 tbsp finely ground oats',
        '2 tbsp cooled green tea',
        '1 tsp honey',
        '1 tsp plain yogurt (if not dairy-sensitive)'
      ],
      instructions: [
        'Brew green tea and let cool completely',
        'Grind oats into fine powder using a blender or food processor',
        'Mix ground oats with cooled green tea to form a paste',
        'Add honey and yogurt (if using)',
        'Apply to clean face in gentle circular motions',
        'Leave on for 10-15 minutes',
        'Rinse with cool water using gentle circular motions',
        'Pat dry and moisturize'
      ],
      duration: '10-15 min',
      science: 'Green tea catechins (EGCG) have powerful anti-inflammatory properties. Oats contain beta-glucans that soothe irritated skin. This combination is ideal for calming active breakouts.',
      tags: ['Anti-inflammatory', 'Soothing', 'Gentle', 'Acne-prone']
    });
  }

  // For oily skin or clogged pores - use region-specific data
  const hasOilySkin = glowScore < 70 || 
    acneSeverity !== 'none' && acneSeverity !== 'very_mild' ||
    hasOilyTexture ||
    totalComedones > 5 ||
    (regionsData.some(r => r.textureDescription.toLowerCase().includes('oily')));
  if (hasOilySkin) {
    masks.push({
      id: 'scan-clay-purify',
      title: 'Clay + Tea Tree Purifying Mask',
      emoji: '🌿',
      description: 'Deep cleansing mask to unclog pores and control excess oil production.',
      benefits: ['Deep cleanse', 'Unclogs pores', 'Oil control', 'Antimicrobial'],
      ingredients: [
        '2 tbsp bentonite or kaolin clay',
        '1-2 drops tea tree oil',
        'Water or rose water to mix',
        '1 tsp apple cider vinegar (optional, for extra cleansing)'
      ],
      instructions: [
        'Mix clay with water or rose water to form a smooth paste',
        'Add tea tree oil and stir well',
        'Add apple cider vinegar if using (test on small area first)',
        'Apply to clean, dry face, avoiding eye area',
        'Let dry for 10-12 minutes (don\'t let it over-dry)',
        'Rinse with lukewarm water using gentle circular motions',
        'Follow with a gentle moisturizer',
        'Use 1-2 times per week maximum'
      ],
      duration: '10-12 min',
      science: 'Clay minerals absorb excess sebum and impurities from pores. Tea tree oil has antimicrobial properties that target acne-causing bacteria. This mask is excellent for oily, acne-prone skin.',
      tags: ['Oil control', 'Deep cleanse', 'Purifying', 'Oily skin']
    });
  }

  // For hyperpigmentation or dark spots - use region-specific data
  const hasHyperpigmentation = maxHyperpigmentation && 
    (maxHyperpigmentation.hyperpigmentationLevel === 'moderate' || 
     maxHyperpigmentation.hyperpigmentationLevel === 'marked' ||
     glowScore < 80);
  if (hasHyperpigmentation) {
    masks.push({
      id: 'scan-turmeric-brighten',
      title: 'Turmeric Brightening Mask',
      emoji: '✨',
      description: 'Helps fade dark spots and even skin tone while reducing inflammation.',
      benefits: ['Brightens dark spots', 'Evens skin tone', 'Anti-inflammatory', 'Antioxidant'],
      ingredients: [
        '1 tsp turmeric powder',
        '1 tsp plain yogurt or honey',
        '1/2 tsp lemon juice (if not sensitive)',
        '1 tsp rice flour (optional, for texture)'
      ],
      instructions: [
        'Mix turmeric with yogurt or honey in a clean bowl',
        'Add lemon juice if using (test on small area first - can be irritating)',
        'Add rice flour if you want a thicker consistency',
        'Apply as spot treatment to dark areas or all over face',
        'Leave on for 10-15 minutes (turmeric can stain!)',
        'Rinse thoroughly with warm water and gentle cleanser',
        'Moisturize after',
        'Use 1-2 times per week'
      ],
      duration: '10-15 min',
      science: 'Curcumin in turmeric inhibits melanin production and has potent anti-inflammatory effects. It helps fade hyperpigmentation and even skin tone naturally. Lemon juice provides vitamin C for additional brightening.',
      tags: ['Brightening', 'Spot treatment', 'Evening tone', 'Hyperpigmentation']
    });
  }

  // For dry or dehydrated skin - use region-specific texture data
  const hasDryness = (estimatedPH && estimatedPH > 5.5) || hasDryTexture || 
    (regionsData.some(r => r.textureDescription.toLowerCase().includes('dry')));
  if (hasDryness) {
    masks.push({
      id: 'scan-hydrating',
      title: 'Avocado + Honey Hydrating Mask',
      emoji: '🥑',
      description: 'Ultra-nourishing mask to restore moisture and support skin barrier health.',
      benefits: ['Deep hydration', 'Restores barrier', 'Nourishes skin', 'Softens'],
      ingredients: [
        '1/4 ripe avocado',
        '1 tbsp raw honey',
        '1 tsp olive oil or jojoba oil',
        '1 tsp aloe vera gel'
      ],
      instructions: [
        'Mash avocado until smooth',
        'Mix in honey, oil, and aloe vera gel',
        'Apply generously to clean face and neck',
        'Relax for 15-20 minutes',
        'Rinse with warm water',
        'Pat dry and follow with moisturizer',
        'Use 2-3 times per week for dry skin'
      ],
      duration: '15-20 min',
      science: 'Avocado is rich in oleic acid and vitamins A, D, and E that deeply nourish skin. Honey acts as a humectant, drawing moisture into the skin. This mask is perfect for restoring hydration to dehydrated skin.',
      tags: ['Hydrating', 'Nourishing', 'Dry skin', 'Barrier repair']
    });
  }

  // Always include a gentle option
  if (masks.length === 0 || glowScore >= 70) {
    masks.push({
      id: 'scan-gentle-oats',
      title: 'Oatmeal + Yogurt Gentle Mask',
      emoji: '🥣',
      description: 'Gentle exfoliating mask that repairs and strengthens your skin barrier.',
      benefits: ['Repairs barrier', 'Gentle exfoliation', 'Calms irritation', 'Maintains health'],
      ingredients: [
        '3 tbsp finely ground oats',
        '2 tbsp plain yogurt (or coconut yogurt for dairy-free)',
        '1 tsp honey'
      ],
      instructions: [
        'Grind oats into fine powder using a blender',
        'Mix with yogurt and honey until smooth',
        'Apply to damp skin in gentle circular motions',
        'Leave on for 10-15 minutes',
        'Rinse with cool water',
        'Pat dry and moisturize',
        'Use 1-2 times per week'
      ],
      duration: '10-15 min',
      science: 'Oats contain beta-glucans that strengthen the skin barrier and reduce inflammation. Yogurt\'s lactic acid gently exfoliates while probiotics support skin health. This is a safe, gentle option for maintaining healthy skin.',
      tags: ['Barrier repair', 'Gentle', 'Exfoliating', 'Maintenance']
    });
  }

  // Always ensure we have at least 6-7 masks for variety
  // Add a universal hydrating mask if we don't have enough
  if (masks.length < 6) {
    masks.push({
      id: 'scan-universal-hydrating',
      title: 'Honey + Oatmeal Hydrating Mask',
      emoji: '🍯',
      description: 'Universal hydrating mask suitable for all skin types. Provides moisture and soothes the skin.',
      benefits: ['Hydrates', 'Soothes', 'Universal', 'Gentle'],
      ingredients: [
        '2 tbsp raw honey',
        '2 tbsp finely ground oats',
        '1 tbsp plain yogurt or aloe vera gel',
        '1 tsp olive oil (optional)'
      ],
      instructions: [
        'Mix honey and ground oats in a clean bowl',
        'Add yogurt or aloe vera gel',
        'Add olive oil if using for extra hydration',
        'Stir until smooth paste forms',
        'Apply to clean, dry face',
        'Leave on for 15-20 minutes',
        'Rinse with warm water',
        'Pat dry and moisturize',
        'Use 1-2 times per week'
      ],
      duration: '15-20 min',
      science: 'Honey is a natural humectant that draws moisture into the skin. Oats provide gentle exfoliation and soothe irritation. This combination works for all skin types and provides essential hydration.',
      tags: ['Hydrating', 'Universal', 'Gentle', 'Soothing']
    });
  }

  // Additional mask 1: Vitamin C Brightening Mask
  masks.push({
    id: 'scan-vitamin-c-brighten',
    title: 'Vitamin C + Papaya Brightening Mask',
    emoji: '🍊',
    description: 'Natural vitamin C mask to brighten skin, fade dark spots, and boost collagen production.',
    benefits: ['Brightens skin', 'Fades dark spots', 'Boosts collagen', 'Antioxidant'],
    ingredients: [
      '2 tbsp fresh papaya (mashed)',
      '1 tsp lemon juice (fresh)',
      '1 tsp honey',
      '1 tsp plain yogurt'
    ],
    instructions: [
      'Mash fresh papaya until smooth',
      'Add lemon juice, honey, and yogurt',
      'Mix well to form a smooth paste',
      'Apply to clean, dry face',
      'Avoid eye area and any open wounds',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Apply sunscreen after (lemon makes skin more sun-sensitive)',
      'Use 1-2 times per week, preferably in the evening'
    ],
    duration: '15-20 min',
    science: 'Papaya contains papain enzyme and vitamin C that naturally exfoliate and brighten skin. Lemon juice provides additional vitamin C for collagen synthesis and dark spot reduction. This mask helps even skin tone and promote a healthy glow.',
    tags: ['Brightening', 'Vitamin C', 'Exfoliating', 'Evening tone']
  });

  // Additional mask 2: Cucumber Cooling Mask
  masks.push({
    id: 'scan-cucumber-cooling',
    title: 'Cucumber + Aloe Cooling Mask',
    emoji: '🥒',
    description: 'Cooling, hydrating mask to soothe irritated skin and reduce puffiness.',
    benefits: ['Cools skin', 'Reduces puffiness', 'Hydrates', 'Soothes irritation'],
    ingredients: [
      '1/2 fresh cucumber (blended)',
      '2 tbsp aloe vera gel',
      '1 tsp rose water',
      '1 tsp honey'
    ],
    instructions: [
      'Blend cucumber until smooth and strain excess water',
      'Mix cucumber pulp with aloe vera gel',
      'Add rose water and honey',
      'Stir until well combined',
      'Apply generously to clean face and neck',
      'Relax for 15-20 minutes',
      'Rinse with cool water',
      'Pat dry and moisturize',
      'Use 2-3 times per week, especially after sun exposure'
    ],
    duration: '15-20 min',
    science: 'Cucumber is 95% water and contains silica, which helps maintain skin elasticity. It has natural cooling properties that reduce inflammation and puffiness. Aloe vera adds extra hydration and anti-inflammatory benefits. This mask is perfect for calming and refreshing tired skin.',
    tags: ['Cooling', 'Hydrating', 'Soothing', 'Refreshing']
  });

  // Additional mask 3: Manuka Honey + Cinnamon Anti-Acne Mask
  masks.push({
    id: 'scan-manuka-cinnamon',
    title: 'Manuka Honey + Cinnamon Anti-Acne Mask',
    emoji: '🍯',
    description: 'Powerful antibacterial mask to fight acne-causing bacteria and reduce inflammation.',
    benefits: ['Fights bacteria', 'Reduces inflammation', 'Heals breakouts', 'Antimicrobial'],
    ingredients: [
      '2 tbsp manuka honey (or raw honey)',
      '1/2 tsp cinnamon powder',
      '1/2 tsp turmeric powder',
      '1 tsp lemon juice (optional, if not sensitive)'
    ],
    instructions: [
      'Mix honey, cinnamon, and turmeric in a clean bowl',
      'Add lemon juice if using (test on small area first)',
      'Stir until smooth paste forms',
      'Apply to clean, dry face, focusing on acne-prone areas',
      'Avoid eye area and any open wounds',
      'Leave on for 15-20 minutes',
      'Rinse with warm water using gentle circular motions',
      'Pat dry and apply gentle moisturizer',
      'Use 2-3 times per week for active breakouts'
    ],
    duration: '15-20 min',
    science: 'Manuka honey contains methylglyoxal (MGO) with strong antibacterial properties that target acne-causing bacteria. Cinnamon has antimicrobial and anti-inflammatory effects. Turmeric\'s curcumin reduces inflammation and redness. This combination is highly effective for treating active acne.',
    tags: ['Anti-acne', 'Antibacterial', 'Healing', 'Anti-inflammatory']
  });

  // Additional mask 4: Rice Water + Niacinamide Brightening Mask
  masks.push({
    id: 'scan-rice-water-brighten',
    title: 'Rice Water + Niacinamide Brightening Mask',
    emoji: '🌾',
    description: 'Natural brightening mask to even skin tone, fade dark spots, and improve skin texture.',
    benefits: ['Brightens skin', 'Fades dark spots', 'Improves texture', 'Evening tone'],
    ingredients: [
      '1/2 cup rice water (soaked overnight)',
      '2 tbsp rice flour',
      '1 tsp honey',
      '1 tsp plain yogurt',
      '1/2 tsp turmeric powder'
    ],
    instructions: [
      'Soak 1/4 cup rice in water overnight, strain to get rice water',
      'Mix rice flour with rice water to form a smooth paste',
      'Add honey, yogurt, and turmeric',
      'Stir until well combined',
      'Apply to clean, dry face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water using gentle circular motions',
      'Pat dry and moisturize',
      'Use 2-3 times per week for best results'
    ],
    duration: '15-20 min',
    science: 'Rice water contains inositol, which promotes cell regeneration and brightens skin. Rice flour provides gentle exfoliation. The combination helps fade hyperpigmentation, even skin tone, and improve overall skin texture. This mask is excellent for addressing dark spots and achieving a brighter complexion.',
    tags: ['Brightening', 'Evening tone', 'Exfoliating', 'Texture improvement']
  });

  // Additional mask 5: Matcha Green Tea Detox Mask
  if (masks.length < 10) {
    masks.push({
      id: 'scan-matcha-detox',
      title: 'Matcha Green Tea Detox Mask',
      emoji: '🍵',
      description: 'Antioxidant-rich mask to detoxify skin and reduce inflammation.',
      benefits: ['Detoxifies', 'Reduces inflammation', 'Antioxidant boost', 'Calming'],
      ingredients: [
        '1 tbsp matcha green tea powder',
        '1 tbsp honey',
        '1 tsp plain yogurt',
        '1/2 tsp turmeric (optional)'
      ],
      instructions: [
        'Mix matcha powder with honey',
        'Add yogurt to create smooth paste',
        'Stir in turmeric if using',
        'Apply evenly to clean face',
        'Leave on for 15-20 minutes',
        'Rinse with lukewarm water'
      ],
      duration: '15-20 min',
      science: 'Matcha contains 137 times more EGCG (epigallocatechin gallate) than regular green tea. This powerful antioxidant reduces inflammation, protects against UV damage, and helps control sebum production.',
      tags: ['Antioxidant', 'Detoxifying', 'Anti-inflammatory', 'Calming']
    });
  }

  // Additional mask 6: Strawberry + Lemon Vitamin C Mask
  if (masks.length < 10) {
    masks.push({
      id: 'scan-strawberry-vitamin-c',
      title: 'Strawberry + Lemon Vitamin C Mask',
      emoji: '🍓',
      description: 'Brightening mask packed with vitamin C to boost collagen and fade dark spots.',
      benefits: ['Boosts collagen', 'Fades dark spots', 'Antioxidant protection', 'Brightening'],
      ingredients: [
        '3-4 fresh strawberries, mashed',
        '1 tsp lemon juice (fresh)',
        '1 tbsp plain yogurt',
        '1 tsp honey'
      ],
      instructions: [
        'Mash strawberries until smooth',
        'Mix with lemon juice and yogurt',
        'Add honey to balance acidity',
        'Apply to clean face',
        'Leave on for 10-15 minutes (don\'t over-dry)',
        'Rinse with cool water, moisturize after'
      ],
      duration: '10-15 min',
      science: 'Strawberries are rich in vitamin C and ellagic acid that brighten skin and protect against UV damage. Lemon juice contains citric acid that gently exfoliates and fades hyperpigmentation.',
      tags: ['Vitamin C', 'Brightening', 'Antioxidant', 'Collagen boost']
    });
  }

  // Ensure we always return at least 10 masks by filling with universal masks if needed
  const universalMasks = [
    {
      id: 'scan-universal-banana-avocado',
      title: 'Banana + Avocado Nourish Mask',
      emoji: '🥑',
      description: 'Ultra-nourishing mask for dry, stressed skin that needs TLC.',
      benefits: ['Deep nourishment', 'Softens skin', 'Vitamins boost', 'Hydrating'],
      ingredients: [
        '1/2 ripe banana',
        '1/4 ripe avocado',
        '1 tsp honey'
      ],
      instructions: [
        'Mash banana and avocado together until smooth',
        'Mix in honey',
        'Apply generously to face and neck',
        'Relax for 15-20 minutes',
        'Rinse with warm water, pat dry'
      ],
      duration: '15-20 min',
      science: 'Avocado is rich in oleic acid and vitamins A, D, and E that deeply nourish skin. Bananas contain potassium and moisture that soften and hydrate.',
      tags: ['Nourishing', 'Dry skin', 'Vitamins', 'Hydrating']
    },
    {
      id: 'scan-universal-chamomile-oat',
      title: 'Chamomile + Oat Soothing Compress',
      emoji: '🌼',
      description: 'Ultra-gentle compress for sensitive, irritated, or post-treatment skin.',
      benefits: ['Calms redness', 'Soothes irritation', 'Anti-inflammatory', 'Gentle'],
      ingredients: [
        '2 chamomile tea bags',
        '1 cup hot water',
        '1 tbsp ground oats',
        'Clean cloth or cotton pads'
      ],
      instructions: [
        'Steep chamomile tea bags in hot water for 10 minutes',
        'Let cool to room temperature',
        'Stir in ground oats',
        'Soak cloth in mixture',
        'Apply compress to face for 15-20 minutes',
        'Pat dry, no need to rinse'
      ],
      duration: '15-20 min',
      science: 'Chamomile contains bisabolol and chamazulene that have powerful anti-inflammatory and antioxidant properties. Oats contain beta-glucans that soothe and repair the skin barrier.',
      tags: ['Sensitive skin', 'Soothing', 'Anti-inflammatory', 'Gentle']
    }
  ];

  // Fill up to 10 masks if we have fewer
  while (masks.length < 10) {
    const universalIndex = (masks.length - 8) % universalMasks.length;
    const universalMask = universalMasks[universalIndex];
    // Ensure unique ID
    masks.push({
      ...universalMask,
      id: `${universalMask.id}-${masks.length}`
    });
  }

  // Filter masks based on skin condition focus
  const filteredMasks = masks.filter(mask => {
    const tags = mask.tags.map(t => t.toLowerCase());
    const title = mask.title.toLowerCase();
    const description = mask.description.toLowerCase();
    const benefits = mask.benefits.map(b => b.toLowerCase()).join(' ');

    if (skinConditionFocus === 'acne-oily') {
      // Plus: Only acne and oily skin masks
      const acneOilyKeywords = ['acne', 'oily', 'oil control', 'purifying', 'anti-acne', 'acne-prone', 'clogged', 'sebum', 'breakout', 'pimple', 'inflammation', 'redness'];
      return tags.some(tag => acneOilyKeywords.some(keyword => tag.includes(keyword))) ||
             acneOilyKeywords.some(keyword => title.includes(keyword) || description.includes(keyword) || benefits.includes(keyword));
    } else if (skinConditionFocus === 'expanded') {
      // Pro: Acne, oily, dryness, sensitivity masks
      const expandedKeywords = ['acne', 'oily', 'oil control', 'purifying', 'anti-acne', 'acne-prone', 'clogged', 'sebum', 'breakout', 'pimple', 'inflammation', 'redness',
                                'dry', 'dryness', 'hydrating', 'moisturizing', 'dehydrated', 'nourishing',
                                'sensitive', 'sensitivity', 'gentle', 'barrier repair', 'soothing', 'irritation'];
      return tags.some(tag => expandedKeywords.some(keyword => tag.includes(keyword))) ||
             expandedKeywords.some(keyword => title.includes(keyword) || description.includes(keyword) || benefits.includes(keyword));
    } else {
      // Elite: All masks (comprehensive - no filtering)
      return true;
    }
  });

  // If filtering removed all masks, keep at least the acne/oily ones for Plus, or all for Pro/Elite
  if (filteredMasks.length === 0 && skinConditionFocus === 'acne-oily') {
    // Fallback: return masks that are at least somewhat related to acne/oily
    const fallbackMasks = masks.filter(mask => {
      const tags = mask.tags.map(t => t.toLowerCase()).join(' ');
      const title = mask.title.toLowerCase();
      return tags.includes('acne') || tags.includes('oily') || tags.includes('oil') || title.includes('acne') || title.includes('oil');
    }).slice(0, Math.max(1, masks.length));
    
    // Shuffle fallback masks using seed for personalization
    const shuffledFallback = seededShuffleMasks(fallbackMasks, maskSeed);
    return shuffledFallback;
  }

  // Shuffle filtered masks using seed to ensure different users get different masks
  // even with similar skin conditions
  const finalMasks = filteredMasks.length > 0 ? filteredMasks : masks;
  return seededShuffleMasks(finalMasks, maskSeed);
}

/**
 * Seeded shuffle function to ensure consistent but personalized mask ordering
 * Different users with similar conditions will get different mask orders
 */
function seededShuffleMasks<T>(array: T[], seed: number): T[] {
  const shuffled = [...array];
  let currentSeed = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const j = Math.floor((currentSeed / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Generates 8 additional personalized DIY face mask recommendations for the detail page
 * These are shown separately from the main recommendations and are personalized based on skin/acne type
 */
export function generateAdditionalDIYMasks(
  glowScore: number,
  acneSeverity: AcneSeverity,
  estimatedPH?: number,
  isInflammatory?: boolean
): DIYMask[] {
  const additionalMasks: DIYMask[] = [];

  // Mask 1: Charcoal Detox Mask (for oily/acne-prone skin)
  if (glowScore < 70 || (acneSeverity !== 'none' && acneSeverity !== 'very_mild')) {
    additionalMasks.push({
      id: 'additional-charcoal-detox',
      title: 'Activated Charcoal + Clay Detox Mask',
      emoji: '⚫',
      description: 'Deep cleansing mask to draw out impurities, excess oil, and toxins from pores.',
      benefits: ['Deep detox', 'Unclogs pores', 'Oil control', 'Purifying'],
      ingredients: [
        '1 tbsp activated charcoal powder',
        '1 tbsp bentonite clay',
        '2 tbsp water or rose water',
        '1 tsp apple cider vinegar',
        '1 tsp honey'
      ],
      instructions: [
        'Mix charcoal and clay powders in a non-metal bowl',
        'Add water or rose water gradually to form a paste',
        'Add apple cider vinegar and honey',
        'Stir until smooth (avoid metal utensils)',
        'Apply to clean, dry face, avoiding eye area',
        'Let dry for 10-15 minutes (don\'t let it over-dry)',
        'Rinse with warm water using gentle circular motions',
        'Follow with a gentle moisturizer',
        'Use once per week maximum'
      ],
      duration: '10-15 min',
      science: 'Activated charcoal has a large surface area that binds to toxins and impurities. Bentonite clay draws out excess sebum and unclogs pores. This powerful combination is excellent for deep cleansing oily, acne-prone skin.',
      tags: ['Detox', 'Deep cleanse', 'Oil control', 'Purifying']
    });
  }

  // Mask 2: Rosehip + Vitamin E Repair Mask (for healing/scarring)
  if (glowScore < 80 || acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    additionalMasks.push({
      id: 'additional-rosehip-repair',
      title: 'Rosehip Oil + Vitamin E Repair Mask',
      emoji: '🌹',
      description: 'Healing mask to reduce scars, hyperpigmentation, and promote skin regeneration.',
      benefits: ['Heals scars', 'Reduces hyperpigmentation', 'Promotes regeneration', 'Anti-aging'],
      ingredients: [
        '1 tbsp rosehip seed oil',
        '1 vitamin E capsule (pierced)',
        '1 tbsp aloe vera gel',
        '1 tsp honey',
        '1/2 tsp turmeric powder'
      ],
      instructions: [
        'Mix rosehip oil with aloe vera gel',
        'Add contents of vitamin E capsule',
        'Add honey and turmeric',
        'Stir until well combined',
        'Apply to clean face, focusing on scarred areas',
        'Gently massage in circular motions for 2 minutes',
        'Leave on for 20-30 minutes',
        'Rinse with warm water',
        'Use 2-3 times per week, preferably at night'
      ],
      duration: '20-30 min',
      science: 'Rosehip oil is rich in retinoic acid and essential fatty acids that promote cell regeneration and reduce scarring. Vitamin E is a powerful antioxidant that supports healing. This combination is excellent for fading scars and hyperpigmentation.',
      tags: ['Healing', 'Scar reduction', 'Anti-aging', 'Regeneration']
    });
  }

  // Mask 3: Probiotic + Yogurt Balancing Mask (for sensitive/irritated skin)
  additionalMasks.push({
    id: 'additional-probiotic-balancing',
    title: 'Probiotic Yogurt + Oatmeal Balancing Mask',
    emoji: '🥛',
    description: 'Balancing mask to restore skin microbiome and reduce inflammation.',
    benefits: ['Restores microbiome', 'Reduces inflammation', 'Balances pH', 'Soothes'],
    ingredients: [
      '3 tbsp plain Greek yogurt (with live cultures)',
      '2 tbsp finely ground oats',
      '1 tsp honey',
      '1 tsp chamomile tea (cooled)',
      '1/2 tsp turmeric powder'
    ],
    instructions: [
      'Grind oats into fine powder',
      'Mix yogurt with ground oats',
      'Add honey, chamomile tea, and turmeric',
      'Stir until smooth paste forms',
      'Apply to clean, dry face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Pat dry and moisturize',
      'Use 2-3 times per week'
    ],
    duration: '15-20 min',
    science: 'Probiotics in yogurt help restore the skin\'s natural microbiome, which is essential for healthy skin barrier function. Oats soothe irritation, while chamomile provides additional anti-inflammatory benefits. This mask is ideal for sensitive, irritated skin.',
    tags: ['Probiotic', 'Balancing', 'Sensitive skin', 'Microbiome']
  });

  // Mask 4: Matcha Green Tea Antioxidant Mask (for all skin types)
  additionalMasks.push({
    id: 'additional-matcha-antioxidant',
    title: 'Matcha Green Tea + Honey Antioxidant Mask',
    emoji: '🍵',
    description: 'Powerful antioxidant mask to protect against environmental damage and brighten skin.',
    benefits: ['Antioxidant protection', 'Brightens skin', 'Fights free radicals', 'Anti-aging'],
    ingredients: [
      '2 tsp matcha green tea powder',
      '2 tbsp raw honey',
      '1 tbsp plain yogurt',
      '1 tsp lemon juice (optional)',
      '1 tsp olive oil'
    ],
    instructions: [
      'Mix matcha powder with honey',
      'Add yogurt and stir well',
      'Add lemon juice if using (test on small area first)',
      'Add olive oil for extra hydration',
      'Apply to clean, dry face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Apply sunscreen after (if used during day)',
      'Use 2-3 times per week'
    ],
    duration: '15-20 min',
    science: 'Matcha contains high levels of EGCG (epigallocatechin gallate), a powerful antioxidant that protects skin from UV damage and free radicals. Honey provides additional antioxidants and antimicrobial properties. This mask helps prevent premature aging and brightens the complexion.',
    tags: ['Antioxidant', 'Brightening', 'Anti-aging', 'Protection']
  });

  // Mask 5: Collagen-Boosting Berry Mask (for mature/damaged skin)
  if (glowScore < 75 || (estimatedPH && estimatedPH > 5.5)) {
    additionalMasks.push({
      id: 'additional-collagen-berry',
      title: 'Berry + Vitamin C Collagen-Boosting Mask',
      emoji: '🫐',
      description: 'Collagen-boosting mask to improve skin elasticity and reduce fine lines.',
      benefits: ['Boosts collagen', 'Improves elasticity', 'Reduces fine lines', 'Firms skin'],
      ingredients: [
        '1/4 cup mixed berries (blueberries, strawberries, raspberries)',
        '1 tbsp plain yogurt',
        '1 tsp honey',
        '1 tsp lemon juice',
        '1/2 tsp vitamin C powder (optional)'
      ],
      instructions: [
        'Mash berries until smooth',
        'Mix with yogurt and honey',
        'Add lemon juice and vitamin C powder if using',
        'Stir until well combined',
        'Apply to clean, dry face and neck',
        'Leave on for 15-20 minutes',
        'Rinse with cool water',
        'Pat dry and moisturize',
        'Use 2-3 times per week'
      ],
      duration: '15-20 min',
      science: 'Berries are rich in vitamin C and antioxidants that stimulate collagen production. Vitamin C is essential for collagen synthesis and helps improve skin elasticity. This mask is excellent for mature skin or skin showing signs of aging.',
      tags: ['Collagen', 'Anti-aging', 'Firming', 'Elasticity']
    });
  }

  // Mask 6: Niacinamide + Zinc Soothing Mask (for inflammatory acne)
  if (isInflammatory || acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    additionalMasks.push({
      id: 'additional-niacinamide-zinc',
      title: 'Niacinamide + Zinc Oxide Soothing Mask',
      emoji: '💊',
      description: 'Anti-inflammatory mask to calm active breakouts and reduce redness.',
      benefits: ['Reduces inflammation', 'Calms breakouts', 'Reduces redness', 'Healing'],
      ingredients: [
        '2 tbsp plain yogurt',
        '1 tsp niacinamide powder (or crushed niacinamide supplement)',
        '1 tsp zinc oxide powder',
        '1 tbsp aloe vera gel',
        '1 tsp honey'
      ],
      instructions: [
        'Mix yogurt with aloe vera gel',
        'Add niacinamide and zinc oxide powders',
        'Add honey and stir until smooth',
        'Apply to clean, dry face, focusing on inflamed areas',
        'Leave on for 15-20 minutes',
        'Rinse with cool water',
        'Pat dry gently and apply gentle moisturizer',
        'Use 2-3 times per week for active breakouts'
      ],
      duration: '15-20 min',
      science: 'Niacinamide reduces inflammation and regulates sebum production. Zinc oxide has anti-inflammatory and antimicrobial properties that help heal active breakouts. This combination is highly effective for calming inflammatory acne.',
      tags: ['Anti-inflammatory', 'Acne treatment', 'Healing', 'Soothing']
    });
  }

  // Mask 7: Hyaluronic Acid Hydrating Mask (for dry/dehydrated skin)
  if (estimatedPH && estimatedPH > 5.5) {
    additionalMasks.push({
      id: 'additional-hyaluronic-hydrating',
      title: 'Hyaluronic Acid + Aloe Hydrating Mask',
      emoji: '💧',
      description: 'Intensive hydrating mask to restore moisture and plump the skin.',
      benefits: ['Deep hydration', 'Plumps skin', 'Restores moisture', 'Smooths'],
      ingredients: [
        '2 tbsp aloe vera gel',
        '1 tsp hyaluronic acid powder (or serum)',
        '1 tbsp glycerin',
        '1 tsp honey',
        '2 tbsp rose water'
      ],
      instructions: [
        'Mix aloe vera gel with rose water',
        'Add hyaluronic acid powder or serum',
        'Add glycerin and honey',
        'Stir until smooth gel forms',
        'Apply generously to clean, damp face',
        'Leave on for 20-30 minutes',
        'Rinse with cool water or leave on as overnight mask',
        'Pat dry and moisturize',
        'Use 2-3 times per week for dry skin'
      ],
      duration: '20-30 min',
      science: 'Hyaluronic acid can hold up to 1000 times its weight in water, providing intense hydration. Glycerin is a humectant that draws moisture into the skin. This combination is perfect for dehydrated or dry skin that needs extra moisture.',
      tags: ['Hydrating', 'Moisturizing', 'Plumping', 'Dry skin']
    });
  }

  // Mask 8: Salicylic Acid + Willow Bark Exfoliating Mask (for clogged pores)
  if (glowScore < 70 || (acneSeverity !== 'none' && acneSeverity !== 'very_mild')) {
    additionalMasks.push({
      id: 'additional-salicylic-exfoliating',
      title: 'Salicylic Acid + Willow Bark Exfoliating Mask',
      emoji: '🌿',
      description: 'Chemical exfoliating mask to unclog pores and remove dead skin cells.',
      benefits: ['Exfoliates', 'Unclogs pores', 'Removes dead skin', 'Smooths texture'],
      ingredients: [
        '2 tbsp willow bark powder (natural source of salicylic acid)',
        '1 tbsp bentonite clay',
        '2 tbsp water or rose water',
        '1 tsp honey',
        '1 tsp apple cider vinegar'
      ],
      instructions: [
        'Mix willow bark powder with clay',
        'Add water or rose water to form paste',
        'Add honey and apple cider vinegar',
        'Stir until smooth (avoid metal utensils)',
        'Apply to clean, dry face, avoiding eye area',
        'Leave on for 10-12 minutes (don\'t over-dry)',
        'Rinse with warm water using gentle circular motions',
        'Follow with gentle moisturizer',
        'Use once per week, avoid if skin is too sensitive'
      ],
      duration: '10-12 min',
      science: 'Willow bark contains salicylic acid, a beta-hydroxy acid (BHA) that penetrates pores to dissolve sebum and dead skin cells. This helps unclog pores and prevent breakouts. Clay provides additional deep cleansing. This mask is excellent for oily, acne-prone skin with clogged pores.',
      tags: ['Exfoliating', 'BHA', 'Pore treatment', 'Texture improvement']
    });
  }

  // Universal masks pool - always available for all skin types
  const universalMasks: DIYMask[] = [
    {
      id: 'additional-universal-nourishing',
      title: 'Avocado + Banana Nourishing Mask',
      emoji: '🥑',
      description: 'Rich, nourishing mask to deeply moisturize and soften the skin.',
      benefits: ['Nourishes', 'Moisturizes', 'Softens', 'Universal'],
      ingredients: [
        '1/4 ripe avocado',
        '1/2 ripe banana',
        '1 tbsp honey',
        '1 tsp olive oil',
        '1 tsp lemon juice'
      ],
      instructions: [
        'Mash avocado and banana until smooth',
        'Add honey, olive oil, and lemon juice',
        'Mix until well combined',
        'Apply generously to clean face and neck',
        'Leave on for 20-25 minutes',
        'Rinse with warm water',
        'Pat dry and moisturize',
        'Use 2-3 times per week'
      ],
      duration: '20-25 min',
      science: 'Avocado is rich in healthy fats and vitamins A, D, and E that deeply nourish the skin. Banana provides potassium and natural enzymes that soften the skin. This combination is perfect for all skin types needing extra nourishment.',
      tags: ['Nourishing', 'Moisturizing', 'Universal', 'Softening']
    },
    {
      id: 'additional-universal-turmeric',
      title: 'Turmeric + Milk Brightening Mask',
      emoji: '🟡',
      description: 'Gentle brightening mask to even skin tone and add radiance.',
      benefits: ['Brightens', 'Evening tone', 'Gentle', 'Universal'],
      ingredients: [
        '1 tsp turmeric powder',
        '2 tbsp milk (or plant-based milk)',
        '1 tsp honey',
        '1 tsp gram flour (besan)'
      ],
      instructions: [
        'Mix turmeric with milk',
        'Add honey and gram flour',
        'Stir until smooth paste forms',
        'Apply to clean, dry face',
        'Leave on for 15-20 minutes',
        'Rinse with cool water',
        'Pat dry and moisturize',
        'Use 2-3 times per week'
      ],
      duration: '15-20 min',
      science: 'Turmeric contains curcumin which has anti-inflammatory and brightening properties. Milk provides lactic acid for gentle exfoliation. This mask helps even skin tone and add a natural glow.',
      tags: ['Brightening', 'Evening tone', 'Gentle', 'Universal']
    },
    {
      id: 'additional-universal-egg',
      title: 'Egg White + Lemon Tightening Mask',
      emoji: '🥚',
      description: 'Pore-tightening and brightening mask for all skin types.',
      benefits: ['Tightens pores', 'Brightens', 'Oil control', 'Universal'],
      ingredients: [
        '1 egg white',
        '1 tsp lemon juice',
        '1 tsp honey'
      ],
      instructions: [
        'Separate egg white from yolk',
        'Whisk egg white until frothy',
        'Add lemon juice and honey',
        'Mix well',
        'Apply to clean, dry face',
        'Leave on for 15-20 minutes until it dries',
        'Rinse with cool water',
        'Pat dry and moisturize',
        'Use 1-2 times per week'
      ],
      duration: '15-20 min',
      science: 'Egg white contains proteins that temporarily tighten pores and reduce oiliness. Lemon juice provides vitamin C for brightening. This mask is suitable for all skin types.',
      tags: ['Pore tightening', 'Brightening', 'Oil control', 'Universal']
    },
    {
      id: 'additional-universal-coconut',
      title: 'Coconut Oil + Brown Sugar Exfoliating Mask',
      emoji: '🥥',
      description: 'Gentle exfoliating and moisturizing mask for smooth, soft skin.',
      benefits: ['Exfoliates', 'Moisturizes', 'Softens', 'Universal'],
      ingredients: [
        '2 tbsp coconut oil',
        '2 tbsp brown sugar',
        '1 tsp honey',
        '1 tsp lemon juice (optional)'
      ],
      instructions: [
        'Mix coconut oil with brown sugar',
        'Add honey and lemon juice if using',
        'Stir until well combined',
        'Apply to clean, damp face',
        'Gently massage in circular motions for 2-3 minutes',
        'Leave on for 10 minutes',
        'Rinse with warm water',
        'Pat dry and moisturize',
        'Use 1-2 times per week'
      ],
      duration: '10-15 min',
      science: 'Brown sugar provides gentle exfoliation to remove dead skin cells. Coconut oil deeply moisturizes and nourishes the skin. This combination leaves skin smooth and soft.',
      tags: ['Exfoliating', 'Moisturizing', 'Softening', 'Universal']
    },
    {
      id: 'additional-universal-green-tea',
      title: 'Green Tea + Honey Soothing Mask',
      emoji: '🍃',
      description: 'Calming and antioxidant-rich mask for all skin types.',
      benefits: ['Soothes', 'Antioxidant', 'Calming', 'Universal'],
      ingredients: [
        '2 green tea bags (brewed and cooled)',
        '2 tbsp honey',
        '1 tbsp plain yogurt',
        '1 tsp aloe vera gel'
      ],
      instructions: [
        'Brew green tea and let cool completely',
        'Mix tea with honey, yogurt, and aloe vera',
        'Stir until smooth',
        'Apply to clean, dry face',
        'Leave on for 15-20 minutes',
        'Rinse with cool water',
        'Pat dry and moisturize',
        'Use 2-3 times per week'
      ],
      duration: '15-20 min',
      science: 'Green tea is rich in antioxidants (EGCG) that protect against free radicals. Honey provides antimicrobial and moisturizing properties. This mask soothes and protects all skin types.',
      tags: ['Soothing', 'Antioxidant', 'Calming', 'Universal']
    }
  ];

  // Fill remaining slots with universal masks to ensure we always have exactly 8 masks
  // We have 5 universal masks available, so we can always fill up to 8
  const usedUniversalIndices = new Set<number>();
  
  while (additionalMasks.length < 8) {
    // Find the next available universal mask that hasn't been used
    let found = false;
    for (let i = 0; i < universalMasks.length; i++) {
      if (!usedUniversalIndices.has(i)) {
        const maskId = universalMasks[i].id;
        // Double-check it's not already in the list (shouldn't happen, but safety check)
        if (!additionalMasks.find(m => m.id === maskId)) {
          additionalMasks.push(universalMasks[i]);
          usedUniversalIndices.add(i);
          found = true;
          break;
        }
      }
    }
    
    // If we've used all universal masks and still need more, cycle through them with variant IDs
    if (!found) {
      const index = (additionalMasks.length - usedUniversalIndices.size) % universalMasks.length;
      const baseMask = universalMasks[index];
      const variantNum = Math.floor((additionalMasks.length - usedUniversalIndices.size) / universalMasks.length) + 1;
      additionalMasks.push({
        ...baseMask,
        id: `${baseMask.id}-variant-${variantNum}`,
        title: `${baseMask.title} (Variant ${variantNum})`
      });
    }
  }

  // Ensure we return exactly 8 masks
  const finalMasks = additionalMasks.slice(0, 8);
  
  // Final safety check - should never trigger, but ensures we always return 8
  if (finalMasks.length < 8) {
    console.warn(`generateAdditionalDIYMasks: Only generated ${finalMasks.length} masks, filling to 8`);
    while (finalMasks.length < 8) {
      const index = finalMasks.length % universalMasks.length;
      finalMasks.push({
        ...universalMasks[index],
        id: `${universalMasks[index].id}-final-${finalMasks.length}`
      });
    }
  }
  
  return finalMasks;
}
